import React, { Fragment, useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import SwipeableViews from 'react-swipeable-views';
import { getOlympResultsForPeriod, clearOlympData } from '../../actions/olympiad';
import { countDate } from '../../actions/date';
import AllTimeOlympiadChampions from '../FranchOlympiadParticipants/AllTimeOlympiadChampions';
import CustomAutocomplete from '../../components/CustomAutocomplete';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Toolbar,
    Tabs,
    Tab,
    Typography,
    Box,
} from '@mui/material';
import { Stack } from '@mui/material';
import { Skeleton } from '@mui/material';
import '../../index.css';

export default function OlympiadParticipants() {
    const [index, setIndex] = useState(0);
    const [studentIdFilter, setStudentIdFilter] = useState('');
    const [studentSurnameFilter, setStudentSurnameFilter] = useState('');
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const olympiad = useSelector(state => state.olympiad);
    const { pathname } = useLocation();
    const parts = pathname.split('/').filter(Boolean);
    const period = parts[parts.length - 1] || null;

    useEffect(() => {
        if (!period) return;
        dispatch(getOlympResultsForPeriod(period));
        return () => {
            dispatch(clearOlympData());
        };
    }, [dispatch, period]);

    const studentIds = useMemo(() => {
        const allData = [
            ...Object.values(olympiad.list || {}).flat(),
            ...Object.values(olympiad.champions || {}).flat(),
        ];
        return [
            ...new Set(
                allData
                    .map(row => row.user_id)
                    .filter(id => id !== undefined && id !== null)
                    .map(String)
            ),
        ];
    }, [olympiad]);

    const studentSurnames = useMemo(() => {
        const allData = [
            ...Object.values(olympiad.list || {}).flat(),
            ...Object.values(olympiad.champions || {}).flat(),
        ];
        return [...new Set(allData.map(row => row.user).filter(name => name))];
    }, [olympiad]);

    const processTable = useCallback(
        (type = false) => {
            const reducer = type === 'champs' ? olympiad.champions : olympiad.list;
            let data = {};
            for (let item in reducer) {
                if (reducer[item].length) {
                    data[`olympiad.${item}`] = reducer[item];
                }
            }
            return (
                <>
                    {data &&
                        Object.keys(data).map((el, idx) => {
                            const filteredRows = data[el].filter(row => {
                                const matchId = studentIdFilter ? String(row.user_id).includes(studentIdFilter) : true;
                                const matchSurname = studentSurnameFilter
                                    ? String(row.user).toLowerCase().includes(studentSurnameFilter.toLowerCase())
                                    : true;
                                return matchId && matchSurname;
                            });

                            if (!filteredRows.length) return null;

                            return (
                                <Paper className="olympiad_table" key={idx}>
                                    <Toolbar>
                                        <Typography variant="h6" id="tableTitle" component="div">
                                            {t(el)}
                                        </Typography>
                                    </Toolbar>
                                    <TableContainer component={Paper}>
                                        <Table aria-label="a dense table">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>№</TableCell>
                                                    <TableCell align="left">
                                                        {t('activeTransactions.studentId')}
                                                    </TableCell>
                                                    <TableCell align="left">{t('olympiad.student')}</TableCell>
                                                    <TableCell align="left">{t('olympiad.right_stat')}</TableCell>
                                                    <TableCell align="left">{t('olympiad.speed')}</TableCell>
                                                    <TableCell align="left">{t('olympiad.group')}</TableCell>
                                                    {type !== 'champs' && (
                                                        <TableCell align="left">
                                                            {t('olympiad.numberOfAttempts')}
                                                        </TableCell>
                                                    )}
                                                    <TableCell align="left">{t('olympiad.date')}</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {filteredRows.map((row, i) => (
                                                    <TableRow key={`${type}.${row.user_id}.${i}`}>
                                                        <TableCell>
                                                            {type === 'champs' ? i + 1 : row.position}
                                                        </TableCell>
                                                        <TableCell align="left">{row.user_id}</TableCell>
                                                        <TableCell align="left">{row.user}</TableCell>
                                                        <TableCell align="left">{row.answer_correct}</TableCell>
                                                        <TableCell align="left">{row.answer_avg_time}</TableCell>
                                                        <TableCell align="left">{row.group.split('|||')[0]}</TableCell>
                                                        {type !== 'champs' && (
                                                            <TableCell align="left">{row.attempts}</TableCell>
                                                        )}
                                                        <TableCell align="left">
                                                            {type !== 'champs'
                                                                ? countDate(row.created)
                                                                : countDate(row.olimp_date)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            );
                        })}
                </>
            );
        },
        [olympiad, t, studentIdFilter, studentSurnameFilter]
    );

    const loading = olympiad && olympiad.list && Object.keys(olympiad.list).length;

    return (
        <Fragment>
            {index !== 2 && (
                <Stack direction="row" spacing={2} marginBottom={2} marginTop={2}>
                    <CustomAutocomplete
                        options={studentIds}
                        label={t('activeTransactions.studentId')}
                        inputValue={studentIdFilter}
                        onInputChange={(e, val) => setStudentIdFilter(val)}
                        onChange={(e, val) => setStudentIdFilter(val || '')}
                    />
                    <CustomAutocomplete
                        options={studentSurnames}
                        label={t('olympiad.student')}
                        inputValue={studentSurnameFilter}
                        onInputChange={(e, val) => setStudentSurnameFilter(val)}
                        onChange={(e, val) => setStudentSurnameFilter(val || '')}
                    />
                </Stack>
            )}

            <Tabs value={index} onChange={(e, value) => setIndex(value)} className="olymp_swipe">
                <Tab label={t('sidebar.olympiad')} />
                <Tab label={t('olympiad.champions')} />
                <Tab label={t('olympiad.victories')} />
            </Tabs>
            <SwipeableViews index={index} onChangeIndex={setIndex}>
                <div>
                    {!loading ? (
                        <>
                            <Skeleton className="olympiad_loading" variant="rect" width={'100%'} height={150} />
                            <Skeleton className="olympiad_loading" variant="rect" width={'100%'} height={150} />
                            <Skeleton className="olympiad_loading" variant="rect" width={'100%'} height={150} />
                        </>
                    ) : (
                        processTable()
                    )}
                </div>
                <div>
                    {!loading ? (
                        <>
                            <Skeleton className="olympiad_loading" variant="rect" width={'100%'} height={150} />
                            <Skeleton className="olympiad_loading" variant="rect" width={'100%'} height={150} />
                            <Skeleton className="olympiad_loading" variant="rect" width={'100%'} height={150} />
                        </>
                    ) : (
                        processTable('champs')
                    )}
                </div>
                <div>
                    <AllTimeOlympiadChampions />
                </div>
            </SwipeableViews>
        </Fragment>
    );
}
